import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRESETS, applyAction, createGame, type Action, type GameEvent, type GameState } from '@ic/engine';
import { activeRuleset } from '../lib/store';
import { Board } from './Board';
import { Icon } from './Icons';

const illegal = PRESETS.find((p) => p.id === 'illegal')!;
/** A showcase game is restarted after this many half-moves, or when it ends. */
const MAX_PLY = 140;

/**
 * Home page showcase: two bots play the selected preset against each other, so a visitor can preview
 * the board before turning it into a game of their own.
 */
export function HeroBoard({ presetId = 'illegal' }: { presetId?: string }) {
  const nav = useNavigate();
  const preset = PRESETS.find((p) => p.id === presetId) ?? illegal;
  const [state, setState] = useState<GameState>(() => createGame(preset));
  const [last, setLast] = useState<{ action: Action; events: GameEvent[]; key: number } | null>(null);
  const worker = useRef<Worker | null>(null);
  const req = useRef(0);

  useEffect(() => {
    setState(createGame(preset));
    setLast(null);
  }, [preset]);

  useEffect(() => {
    // Game over (or too long): a short pause, then a fresh game.
    if (state.result || state.ply >= MAX_PLY) {
      const t = setTimeout(() => { setState(createGame(preset)); setLast(null); }, 3500);
      return () => clearTimeout(t);
    }
    const id = ++req.current;
    worker.current ??= new Worker(new URL('../workers/bot.worker.ts', import.meta.url), { type: 'module' });
    const w = worker.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onMsg = (e: MessageEvent<{ id: number; action: Action | null }>) => {
      if (e.data.id !== id) return;
      const a = e.data.action;
      // No move left: start over.
      if (!a) { timer = setTimeout(() => { setState(createGame(preset)); setLast(null); }, 1500); return; }
      timer = setTimeout(() => {
        const r = applyAction(state, a);
        setState(r.state);
        setLast((l) => ({ action: a, events: r.events, key: (l?.key ?? 0) + 1 }));
      }, state.ply === 0 ? 1200 : 900);
    };
    w.addEventListener('message', onMsg);
    w.postMessage({ id, state, level: 3 });
    return () => { w.removeEventListener('message', onMsg); if (timer) clearTimeout(timer); };
  }, [state, preset]);

  useEffect(() => () => worker.current?.terminate(), []);

  const fullGame = () => {
    activeRuleset.set(JSON.parse(JSON.stringify(preset)));
    nav('/play/bot?level=2&color=w&tc=0+0');
  };

  return (
    <div className="hero-board">
      <Board state={state} orientation="w" silent lastAction={last?.action} events={last?.events} eventsKey={last?.key} />
      <div className="hero-coach">
        <span className="hero-text"><span className="live-dot" /> {preset.name} · {state.turn === 'w' ? 'White' : 'Black'} to move</span>
        <button className="btn primary sm" onClick={fullGame}>Play this board <Icon name="arrow" size={14} /></button>
      </div>
    </div>
  );
}
